import { Injector } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  Subscription,
} from 'rxjs';
import { fabric } from 'fabric';
import { FrameItem, WebViewerState } from '../../../../models/session';
import { WebViewerComponent } from '../../iframe/web-viewer/web-viewer.component';
import { BoardItemsInteractionsManager } from '../board-items-interactions-manager';
import { isBoardItem } from '../../../common/y-session';
import { INTERACTIONS_BAR_HEIGHT } from '../../iframe/additional-apps.utils';
import { ItemModel } from '../../../../models/itemModel';
import { FLAGS, FlagsService } from '../../../../services/flags.service';
import { SpaceRepository } from '../../../../state/space.repository';
import { UserService } from '../../../../services/user.service';
import { SessionView } from '../../../../services/session-shared-data.service';
import { SpaceZoomCutoffService } from '../../../../services/space-zoom-cutoff.service';
import { BoardItemConfig, CreationContext } from './board-item-configurations';
import { WebViewerConfigurations } from './board-web-viewer-configurartions';
import { BoardItemComponentClass, FabricControls, MAX_BOARD_ITEMS_COUNT } from './board.item';

const BoardAppInvisibleControls = [
  FabricControls.RotateBottomLeft1,
  FabricControls.RotateBottomLeft2,
  FabricControls.RotateBottomRight1,
  FabricControls.RotateBottomRight2,
  FabricControls.RotateTopLeft1,
  FabricControls.RotateTopLeft2,
  FabricControls.RotateTopRight1,
  FabricControls.RotateTopRight2,
  FabricControls.MiddleTop,
  FabricControls.MiddleBottom,
  FabricControls.MiddleLeft,
  FabricControls.MiddleRight,
];
export interface BoardAppConfigurations extends BoardItemConfig {
  isPrivate: boolean;
  interactionsLocked: boolean;
}

export abstract class BoardApp<
  T extends BoardAppConfigurations,
> extends BoardItemComponentClass<any>(fabric.Rect) {
  private boardItemsInteractionsManagerService: BoardItemsInteractionsManager;
  private userPermissionsSubscription?: Subscription;
  private spaceRepo: SpaceRepository;
  protected userService: UserService;
  private spaceZoomCutoffService: SpaceZoomCutoffService;

  private fullScreenIdSubscription?: Subscription;
  private selectionSubscription?: Subscription;
  private fullScreenSubscription?: Subscription;
  private zoomCutOffSubscription?: Subscription;

  private isInFullscreenAppView$ = new BehaviorSubject<boolean>(false);

  constructor(
    injector: Injector,
    itemModel: ItemModel,
    configurations: T,
    creationContext: CreationContext,
  ) {
    super(injector, itemModel, configurations, BoardAppInvisibleControls, creationContext);

    this.transparentCorners = true;
    this.hasRotatingPoint = false;
    this.hasBorders = false;
    this.selectable = true;
    this.cornerSize = 30;
    this.cornerStrokeColor = 'transparent';
    this.multiSelectable = false;
    this.duplicatable = false;
    this.focusOnInsertion = creationContext.isNewlyCreatedItem;

    this.boardItemsInteractionsManagerService = injector.get<BoardItemsInteractionsManager>(
      BoardItemsInteractionsManager,
    );
    this.spaceRepo = injector.get(SpaceRepository);
    this.userService = injector.get(UserService);
    this.flagsService = injector.get(FlagsService);
    this.spaceZoomCutoffService = injector.get(SpaceZoomCutoffService);
  }

  override openLock() {
    this.lockMovementX = false;
    this.lockMovementY = false;
    this.lockRotation = true;
    this.lockScalingX = false;
    this.lockScalingY = false;
    this.lockUniScaling = false;
    this.lockSkewingX = true;
    this.lockSkewingY = true;
    this.lockScalingFlip = true;
  }

  override getPropertiesToInclude(): string[] {
    const parentProperties = super.getPropertiesToInclude();
    return [...parentProperties, 'configurations'];
  }

  override onFabricAdded() {
    super.onFabricAdded();

    if (!this.isOnStaticCanvas()) {
      this.subscribeToFullScreenState();
      this.subscribeToSelectionState();
      this.subscribeToUserPermissions();
      this.setAppPrivacy();
      this.setUpSelectionChangesBasedOnZoomLevel();
    }
  }

  override onFabricRemoved() {
    super.onFabricRemoved();

    // Full-screened app must reset the session view on its removal
    if (this.isInFullscreenAppView$.getValue()) {
      this.sharedDataService.sessionView.switchToSessionView(SessionView.WHITEBOARD);
    }

    this.fullScreenIdSubscription?.unsubscribe();
    this.selectionSubscription?.unsubscribe();
    this.isInFullscreenAppView$.unsubscribe();
    this.fullScreenSubscription?.unsubscribe();
    this.userPermissionsSubscription?.unsubscribe();
    this.zoomCutOffSubscription?.unsubscribe();
  }

  override shouldEnterEditMode(mouseY: number, itemY: number, itemSelected: boolean): boolean {
    return true;
  }

  override setStateFromConfigurations(configurations: T) {
    super.setStateFromConfigurations(configurations);
    this.setAppPrivacy();
  }

  subscribeToFullScreenState() {
    this.fullScreenIdSubscription?.unsubscribe();
    this.fullScreenIdSubscription = this.sharedDataService.sessionView.current$.subscribe(
      (sessionView) => {
        const fullscreenId = sessionView.sessionViewMetaData?.fullscreenId;
        this.isInFullscreenAppView$.next(
          sessionView.view === SessionView.FULLSCREEN_APP &&
            fullscreenId === this.configurations.uid,
        );
      },
    );

    this.fullScreenSubscription?.unsubscribe();
    this.fullScreenSubscription = this.isInFullscreenAppView$.subscribe((isFullScreened) =>
      this.adaptBoardAppStyleToFullScreen(isFullScreened),
    );
  }

  adaptBoardAppStyleToFullScreen(isFullScreened: boolean) {
    if (!isFullScreened) {
      this.keepHtmlSyncedWithFabric = true;
      this.syncHtmlWithFabric();
      return;
    }

    // When the web viewer is full-screened, the width and height should not be determined by the fabric object size anymore
    this.keepHtmlSyncedWithFabric = false;
    this.deSyncHtmlWithFabric();

    // The container should take the whole available space when the app is full-screened
    // So that the part under the VCT don't show the board canvas
    // The width and height of the content is determined by WebViewerComponent
    const htmlElement = this.componentRef?.location?.nativeElement;
    if (htmlElement) {
      htmlElement.style.width = '100%';
      htmlElement.style.height = '100%';
      htmlElement.style.background = 'white';
      htmlElement.style.pointerEvents = 'all'; // so the user doesn't interact with the canvas by mistake
    }

    this.boardItemsInteractionsManagerService.triggerDeselectAll();
    this.canvas?.setActiveObject(this).requestRenderAll();
  }

  subscribeToSelectionState() {
    this.selectionSubscription?.unsubscribe();
    this.selectionSubscription = combineLatest([
      this.boardItemsInteractionsManagerService.selection$,
      this.sharedDataService.sessionView.current$.pipe(
        map(
          (sessionView) =>
            sessionView.view === SessionView.FULLSCREEN_APP &&
            sessionView.sessionViewMetaData?.fullscreenId === this.configurations.uid,
        ),
      ),
    ]).subscribe(([selection, isFullScreened]) => {
      if (
        !isFullScreened && // we don't need to show the scribbles behind the full-screened app as it becomes the whole visible boar
        selection &&
        !selection.isMultiSelection &&
        isBoardItem(selection.object) &&
        selection.object.configurations.uid === this.configurations.uid
      ) {
        this.toggleMixBlendMode(true);
      } else {
        this.toggleMixBlendMode(false);
      }
    });
  }

  private subscribeToUserPermissions() {
    this.userPermissionsSubscription?.unsubscribe();
    this.userPermissionsSubscription = this.spaceRepo.isCurrentUserHost$.subscribe(() =>
      this.lockBoardItemIfNecessary(),
    );
  }

  private setAppPrivacy() {
    const shouldHideApp = this.shouldHideApp();
    if (this.componentRef) {
      this.componentRef.location.nativeElement.style.display = shouldHideApp ? 'none' : 'initial';
    }
    // Hidden app should have no affect on the cursor style when hovering on it
    this.hoverCursor = this.canvas?.hoverCursor;

    // Should behave just like it wasn't added to the canvas
    this.evented = !shouldHideApp; // to ensure no events propagate to it
    this.selectable = !shouldHideApp; // necessary to insure it is not selectable, so it doesn't get set as an active object

    // To prevent interacting with the private web viewer,
    // We could have used the selectable property to prevent interactions instead, but this is being override by the toolbar tools
    this.lockBoardItemIfNecessary();

    // When an app is private, we need to stop the full-screen mode to remove all its affect on the space, hiding the app is not enough.
    if (shouldHideApp && this.isInFullscreenAppView$.getValue()) {
      this.sharedDataService.sessionView.switchToSessionView(SessionView.WHITEBOARD);
    }
  }

  public shouldHideApp(): boolean {
    const config = this.configurations;
    const appCreatorId = config.ownerId;
    const isAppOwner = this.userService.userId === appCreatorId;
    const privateSwitchEnabled = this.flagsService.isFlagEnabled(FLAGS.APPS_PRIVACY_SWITCH);
    const appPrivate = config.isPrivate;
    return privateSwitchEnabled && appPrivate && !isAppOwner;
  }

  private setUpSelectionChangesBasedOnZoomLevel() {
    this.zoomCutOffSubscription?.unsubscribe();
    // No events should propagate to the object once it is in the cutoff state,
    // Preventing moving the apps when they are cut-off
    this.zoomCutOffSubscription = this.spaceZoomCutoffService.shouldCutOff$.subscribe(
      (shouldCutOff) => {
        // We are using evented here instead of selectable, because selectable has some bug with the old implementation
        this.evented = !shouldCutOff;
      },
    );
  }

  private lockBoardItemIfNecessary() {
    const shouldHideApp = this.shouldHideApp();
    if (shouldHideApp || !this.spaceRepo.isCurrentUserHost()) {
      this.closeLock();
    } else {
      this.configureLocalLockState();
    }
    this.canvas?.requestRenderAll();
  }

  toggleMixBlendMode(enable: boolean) {
    if (!this.componentRef) {
      return;
    }
    this.componentRef.location.nativeElement.style.mixBlendMode = enable ? 'soft-light' : 'normal';
  }

  getZIndexObservable$(): Observable<number> {
    return combineLatest([
      this.boardItemsInteractionsManagerService.itemInEditMode$(this.configurations.uid),
      this.itemOrder$,
      this.isInFullscreenAppView$,
    ]).pipe(
      map(([interactionsEnabled, itemOrder, isFullScreened]) => {
        if (this.isOnStaticCanvas()) {
          return itemOrder;
        }
        if (interactionsEnabled || isFullScreened) {
          return MAX_BOARD_ITEMS_COUNT + 1;
        }
        const index = itemOrder - MAX_BOARD_ITEMS_COUNT - 1;
        return Math.min(-1, index);
      }),
      distinctUntilChanged(),
    );
  }

  protected onMouseMove(e) {
    super.onMouseMove(e);

    if (!e.target?.type || !e.pointer || this.canvas?.selection === false) {
      return;
    }

    // Making the hover cursor indicate the moving ability, if not movable set it to the default one
    if (
      this.mouseOnInteractionBar(e.pointer.y, e.target.top ?? 0) &&
      !this.lockMovementX &&
      !this.lockMovementY &&
      !this.shouldHideApp()
    ) {
      e.target['hoverCursor'] = 'all-scroll';
    } else {
      e.target['hoverCursor'] = 'url(../../../../assets/icons/main-cursor.svg), default';
    }
  }

  mouseOnInteractionBar(mouseY: number, itemY: number): boolean {
    return mouseY <= itemY + INTERACTIONS_BAR_HEIGHT;
  }
}

export class BoardWebViewer extends BoardApp<WebViewerConfigurations> {
  AngularComponentClass = WebViewerComponent;
  constructor(
    injector: Injector,
    configurations: WebViewerConfigurations,
    creationContext: CreationContext,
  ) {
    super(injector, ItemModel.WebViewer, configurations, creationContext);
  }

  bindAngularComponentInputs() {
    if (!this.componentRef) {
      return;
    }
    this.componentRef.setInput('state', this.configurations);
    this.componentRef.setInput('static', this.isOnStaticCanvas());
    this.componentRef.setInput('privateMode', this.shouldHideApp());
    this.componentRef.changeDetectorRef.detectChanges();
  }

  override toFrameItem(): FrameItem {
    const encodedBoardItem = super.toFrameItem();
    return {
      ...encodedBoardItem,
      model: ItemModel.WebViewer,
      browser_state: new WebViewerState({
        hbSessionId: this.configurations.hyperbeamSessionId,
        url: this.configurations.embedUrl,
        lastVisitedUrl: this.configurations.lastVisitedUrl,
        type: this.configurations.appInstanceId,
        title: this.configurations.title,
        defaultUrl: this.configurations.startUrl,
        webgl: this.configurations.webGLEnabled,
        initialZoom: this.configurations.initialZoom,
        country: this.configurations.country,
        adblock: this.configurations.adBlockEnabled,
        isPortalSession: this.configurations.isPortalSession,
        remoteControlToken: this.configurations.remoteControlToken,
      }),
      interactionsLocked: this.configurations.interactionsLocked,
      available: this.configurations.available,
      hasAvailabilityNotification: this.configurations.hasAvailabilityNotification,
      isPrivate: this.configurations.isPrivate,
    };
  }
}
